import React, { createRef, MutableRefObject, ReactElement } from 'react';
import Canvas, { ICanvasProps } from './Canvas';
import ColumnUtils from './ColumnUtils';
import { getGridState, getNextScrollState } from './utils/viewportUtils';
import { ICellMetaDataShape } from './PropTypeShapes/CellMetaDataShape';
import { IRowSelection } from './ReactDataGrid';

type IViewportProps = {
  rowOffsetHeight: number;
  totalWidth: number | string;
  columnMetrics: any;
  rowGetter: ICanvasProps['rowGetter'];
  selectedRows?: Array<any>;
  rowSelection: IRowSelection['selectBy'];
  expandedRows?: Array<any>;
  rowRenderer?: ReactElement | Function | unknown; //|| Object - Could just put 'any' here, but probably best to 'try' to be accurate.
  rowsCount: number;
  rowHeight: number;
  onRows?: () => void;
  onScroll?: Function;
  minHeight?: number;
  cellMetaData?: ICellMetaDataShape;
  rowKey: string;
  rowScrollTimeout?: number;
  scrollToRowIndex?: number;
  contextMenu?: ReactElement;
  contextKey?: string;
  getSubRowDetails?: (row: any) => any;
  rowGroupRenderer?: Function;
  disableVirtualRows?: boolean;
  testId?: string;
};

class Viewport extends React.Component<IViewportProps> {
  static displayName = 'Viewport';

  static defaultProps = {
    rowHeight: 30,
  };

  state: ReturnType<typeof getGridState> & { isScrolling?: boolean } = getGridState(this.props);
  canvas: MutableRefObject<any> = createRef();
  viewport: MutableRefObject<any> = createRef();
  resetScrollStateTimeoutId?: NodeJS.Timeout;

  onScroll = (scroll: { scrollTop: number; scrollLeft: number }) => {
    if (this.props.disableVirtualRows) return;

    this.updateScroll(
      this.props,
      scroll.scrollTop,
      scroll.scrollLeft,
      this.state.height,
      this.props.rowHeight,
      this.props.rowsCount
    );

    if (this.props.onScroll) {
      this.props.onScroll({
        scrollTop: scroll.scrollTop,
        scrollLeft: scroll.scrollLeft,
      });
    }
  };

  getScroll = (): { scrollLeft: number; scrollTop: number } => {
    return this.canvas.current.getScroll();
  };

  setScrollLeft = (scrollLeft: number) => {
    this.canvas.current.setScrollLeft(scrollLeft);
  };

  getDOMNodeOffsetWidth = () => {
    return this.viewport.current?.offsetWidth ?? 0;
  };

  clearScrollTimer = () => {
    if (this.resetScrollStateTimeoutId) {
      clearTimeout(this.resetScrollStateTimeoutId);
    }
  };

  resetScrollStateAfterDelay = () => {
    this.clearScrollTimer();
    this.resetScrollStateTimeoutId = setTimeout(this.resetScrollStateAfterDelayCallback, 500);
  };

  resetScrollStateAfterDelayCallback = () => {
    this.resetScrollStateTimeoutId = undefined;
    this.setState({
      isScrolling: false,
    });
  };

  updateScroll = (
    { columnMetrics, disableVirtualRows, overScan }: any,
    scrollTop: number,
    scrollLeft: number,
    height: number,
    rowHeight: number,
    length: number,
    width?: number
  ) => {
    this.resetScrollStateAfterDelay();
    const nextScrollState = getNextScrollState(
      { columnMetrics, disableVirtualRows, overScan },
      this.getDOMNodeOffsetWidth,
      scrollTop,
      scrollLeft,
      height,
      rowHeight,
      length,
      width
    );

    this.setState(nextScrollState);
  };

  metricsUpdated = () => {
    const height = this.viewportHeight();
    const width = this.viewportWidth();
    if (height) {
      this.updateScroll(
        this.props,
        this.state.scrollTop,
        this.state.scrollLeft,
        height,
        this.props.rowHeight,
        this.props.rowsCount,
        width
      );
    }
  };

  viewportHeight = (): number => {
    return this.viewport.current?.offsetHeight ?? 0;
  };

  viewportWidth = (): number => {
    return this.viewport.current?.offsetWidth ?? 0;
  };

  componentDidUpdate(prevProps: IViewportProps) {
    if (prevProps.rowHeight !== this.props.rowHeight || prevProps.minHeight !== this.props.minHeight) {
      const newState = getGridState(this.props);
      this.updateScroll(
        this.props,
        newState.scrollTop,
        newState.scrollLeft,
        newState.height,
        this.props.rowHeight,
        this.props.rowsCount
      );
    } else if (
      prevProps.rowsCount !== this.props.rowsCount ||
      ColumnUtils.getSize(prevProps.columnMetrics.columns) !== ColumnUtils.getSize(this.props.columnMetrics.columns)
    ) {
      this.updateScroll(
        this.props,
        this.state.scrollTop,
        this.state.scrollLeft,
        this.state.height,
        this.props.rowHeight,
        this.props.rowsCount
      );
      // Added to fix the hiding of the bottom scrollbar when showing the filters.
    } else if (prevProps.rowOffsetHeight !== this.props.rowOffsetHeight) {
      // The value of height can be positive or negative and will be added to the current height to cater for changes in the header height (due to the filer)
      const height = prevProps.rowOffsetHeight - this.props.rowOffsetHeight;

      this.updateScroll(
        this.props,
        this.state.scrollTop,
        this.state.scrollLeft,
        this.state.height + height,
        this.props.rowHeight,
        this.props.rowsCount
      );
    }
  }

  componentDidMount() {
    if (window.addEventListener) {
      window.addEventListener('resize', this.metricsUpdated);
    } else {
      //Probably unnecessary, wouldn't expect modern/supported browsers to require this fallback.
      (window as any).attachEvent?.('resize', this.metricsUpdated);
    }
    this.metricsUpdated();
  }

  componentWillUnmount() {
    if (window.removeEventListener) {
      window.removeEventListener('resize', this.metricsUpdated);
    } else {
      //Probably unnecessary, wouldn't expect modern/supported browsers to require this fallback.
      (window as any).detachEvent?.('resize', this.metricsUpdated);
    }
    this.clearScrollTimer();
  }

  render() {
    const style: React.CSSProperties = {
      padding: 0,
      bottom: 0,
      left: 0,
      right: 0,
      overflow: 'hidden',
      position: 'absolute',
      top: this.props.rowOffsetHeight,
    };
    return (
      <div className="react-grid-Viewport" style={style} ref={this.viewport}>
        <Canvas
          ref={this.canvas}
          rowKey={this.props.rowKey}
          totalWidth={this.props.totalWidth}
          width={this.props.columnMetrics.width}
          rowGetter={this.props.rowGetter}
          rowsCount={this.props.rowsCount}
          selectedRows={this.props.selectedRows}
          expandedRows={this.props.expandedRows}
          columns={this.props.columnMetrics.columns}
          rowRenderer={this.props.rowRenderer}
          displayStart={this.state.displayStart}
          displayEnd={this.state.displayEnd}
          visibleStart={this.state.visibleStart}
          visibleEnd={this.state.visibleEnd}
          colVisibleStart={this.state.colVisibleStart}
          colVisibleEnd={this.state.colVisibleEnd}
          colDisplayStart={this.state.colDisplayStart}
          colDisplayEnd={this.state.colDisplayEnd}
          cellMetaData={this.props.cellMetaData}
          height={this.state.height}
          rowHeight={this.props.rowHeight}
          onScroll={this.onScroll}
          onRows={this.props.onRows}
          rowScrollTimeout={this.props.rowScrollTimeout}
          scrollToRowIndex={this.props.scrollToRowIndex}
          contextMenu={this.props.contextMenu}
          contextKey={this.props.contextKey}
          rowSelection={this.props.rowSelection}
          getSubRowDetails={this.props.getSubRowDetails}
          rowGroupRenderer={this.props.rowGroupRenderer}
          isScrolling={this.state.isScrolling || false}
          testId={this.props.testId}
          disableVirtualRows={this.props.disableVirtualRows}
        />
      </div>
    );
  }
}

export default Viewport;
